import styled from 'styled-components';
import { JoinInput, JoinPhoneInput } from '../../components/items/joinInput';
import { Button } from '../../components/items/Button';
import { CommonTitle } from '../../components/items/common.title';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { AlertModal } from '../../components/modal/alert.modal';
import { phoneChangeSchema } from '../../schema/mypageSchema';
import { changeUserPhone, chargeCompanyPhone } from '../../api/mypage';
import { requestPhoneAuth, verifyPhoneAuth } from '../../api/join';
import { formatPhoneNumber } from '../../utils/util';
import { useHistory, useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { authState } from '../../atoms/authState';

interface ChangePhoneProps {
  phone: string;
  authCode?: string; // 인증번호 필드 추가
  changedPhone: string;
}

const PhoneChange = () => {
  const auth = useRecoilValue(authState);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [phoneAuthUUID, setPhoneAuthUUID] = useState<string | null>(null); // 인증 UUID 상태 추가
  const [isPhoneVerified, setIsPhoneVerified] = useState(false); // 전화번호 인증 여부 상태 추가
  const [isPhoneDisabled, setIsPhoneDisabled] = useState(false);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null); // 타이머 상태 추가
  const [remainingTime, setRemainingTime] = useState(180); // 남은 시간(초) 상태 추가
  const [navigateOnClose, setNavigateOnClose] = useState(false); // 페이지 이동 여부를 추적
  const history = useHistory(); // useHistory 훅 사용

  console.log(auth);

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<ChangePhoneProps>({
    resolver: zodResolver(phoneChangeSchema),
  });

  const startTimer = () => {
    if (timer) {
      clearTimeout(timer);
    }

    setRemainingTime(180); // 타이머 시작 시 3분 설정

    const newTimer = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(newTimer);
          setPhoneAuthUUID(null);
          setModalMessage('인증 시간이 만료되었습니다. 다시 시도해주세요.');
          setIsModalOpen(true);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000); // 1초마다 타이머 업데이트

    setTimer(newTimer);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (navigateOnClose) {
      history.push({
        pathname: '/mypage',
      }); // navigateOnClose가 true일 때만 /mypage로 이동
    }
  };

  const phoneAuth = async () => {
    try {
      const phone = getValues('phone');
      if (phone) {
        const uuid = await requestPhoneAuth(phone, 'driver');
        setPhoneAuthUUID(uuid);
        setModalMessage('인증번호가 전송되었습니다. 번호를 확인해주세요.');
        setIsModalOpen(true);
        startTimer(); // 타이머 시작
      } else {
        setModalMessage('휴대폰 번호를 입력해주세요.');
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error('휴대폰 본인 인증 요청 실패:', error);
      setModalMessage('본인 인증 요청에 실패했습니다.');
      setIsModalOpen(true);
    }
  };

  const verifyAuthCode = async (authCode: string) => {
    if (!phoneAuthUUID) {
      setModalMessage('인증 시간이 만료되었습니다. 다시 시도해주세요.');
      setIsModalOpen(true);
      return;
    }

    try {
      const message = await verifyPhoneAuth(phoneAuthUUID, authCode);
      setModalMessage('본인 인증에 성공하였습니다.');
      setIsModalOpen(true);
      setIsPhoneVerified(true);
      setIsPhoneDisabled(true);

      if (timer) {
        clearTimeout(timer); // 인증 성공 시 타이머 해제
      }
    } catch (error) {
      console.error('인증 실패:', error);
      setModalMessage('본인 인증 요청에 실패하였습니다.');
      setIsModalOpen(true);
    }
  };

  const onSubmit = async (data: ChangePhoneProps) => {
    if (!isPhoneVerified) {
      setModalMessage('휴대폰 번호 인증을 완료해주세요.');
      setIsModalOpen(true);
      return; // 인증이 완료되지 않은 경우 회원가입 진행 중단
    }

    try {
      let response;

      if (auth.userType === 'driver') {
        response = await changeUserPhone(data);
      } else if (auth.userType === 'company') {
        response = await chargeCompanyPhone(data);
      }

      if (response && response.statusCode === 200) {
        setModalMessage(response.message);
        setNavigateOnClose(true); // 휴대폰번호 변경이 성공하면 페이지 이동 설정
      } else {
        setModalMessage('휴대폰번호 변경에 실패했습니다.');
        setNavigateOnClose(false); // 실패 시 페이지 이동을 하지 않도록 설정
      }
    } catch (error) {
      setModalMessage('휴대폰번호 변경 중 오류가 발생했습니다.');
      setNavigateOnClose(false); // 오류 발생 시 페이지 이동을 하지 않도록 설정
    } finally {
      setIsModalOpen(true);
    }
  };

  return (
    <Container>
      <CommonTitle
        title="휴대폰 변경"
        subTitle={`휴대폰 번호는 인증절차가 \n필요합니다.`}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <JoinPhoneInput
          name="phone"
          label="휴대폰 번호"
          placeholder="휴대폰 번호 입력"
          type="text"
          btn
          btnTitle="본인인증"
          disabled={isPhoneDisabled}
          errors={errors.phone?.message ? [errors.phone.message as string] : []}
          register={register}
          validation={{
            onChange: (e: any) => {
              const formattedValue = formatPhoneNumber(e.target.value);
              setValue('phone', formattedValue, { shouldValidate: true });
            },
          }}
          onClick={phoneAuth}
          className={`input-disabled ${isPhoneDisabled ? 'disabled' : ''}`}
        />
        {/* 인증 번호 입력 필드 */}
        <div className="timer-wrapper">
          <JoinInput
            name="authCode"
            label="인증번호"
            placeholder="인증번호 입력"
            type="text"
            btn
            btnTitle="인증확인"
            register={register}
            onClick={() => verifyAuthCode(getValues('authCode') || '')}
          />
          <p>
            남은 시간: {Math.floor(remainingTime / 60)}:
            {remainingTime % 60 < 10
              ? `0${remainingTime % 60}`
              : remainingTime % 60}
          </p>
        </div>
        <JoinPhoneInput
          name="changedPhone"
          label="변경 휴대폰 번호"
          placeholder="휴대폰 번호 입력"
          type="text"
          errors={
            errors.changedPhone?.message
              ? [errors.changedPhone.message as string]
              : []
          }
          register={register}
          validation={{
            onChange: (e: any) => {
              const formattedValue = formatPhoneNumber(e.target.value);
              setValue('phone', formattedValue, { shouldValidate: true });
            },
          }}
        />
        <Button title="휴대폰 번호 변경" type="submit" fiexd />
      </form>
      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose} // 회원가입 완료 시에만 로그인 화면으로 이동
        />
      )}
    </Container>
  );
};

export default PhoneChange;

// style
const Container = styled.div`
  padding: 2.4rem 2rem;
  form {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    .input-disabled {
      position: relative;
      &.disabled {
        opacity: 0.4;
        > div {
          background-color: #f0f0f0;
        }
        &::after {
          content: '';
          top: 0;
          left: 0;
          position: absolute;
          width: 100%;
          height: 100%;
          background-color: black;
          opacity: 0.2;
          border-radius: 0.8rem;
        }
      }
    }
    .timer-wrapper {
      position: relative;
      p {
        position: absolute;
        bottom: 2rem;
        right: 8rem;
        font-size: 1.3rem;
        width: 8.5rem;
      }
    }
  }
`;
